/**
 * Article Download with Klaviyo Email Gate
 * Handles modal display, email form submission to Klaviyo, and PDF download
 */
document.addEventListener('DOMContentLoaded', () => {
  // Initialize all download modals on the page
  const modalTriggers = document.querySelectorAll('[data-modal-trigger]');
  const modalCloseButtons = document.querySelectorAll('[data-modal-close]');
  const downloadForms = document.querySelectorAll('.js-download-form');

  // Open modal when trigger button is clicked
  modalTriggers.forEach(trigger => {
    trigger.addEventListener('click', e => {
      e.preventDefault();
      const modalId = trigger.getAttribute('data-modal-trigger');
      const modal = document.getElementById(modalId);
      if (modal) {
        modal.classList.remove('hidden');
        modal.classList.add('flex', 'items-center', 'justify-center');
        // Focus on email input for accessibility
        const emailInput = modal.querySelector('input[type="email"]');
        if (emailInput) {
          setTimeout(() => emailInput.focus(), 100);
        }
        // Prevent body scroll when modal is open
        document.body.style.overflow = 'hidden';
      }
    });
  });

  // Close modal when close button is clicked
  modalCloseButtons.forEach(closeBtn => {
    closeBtn.addEventListener('click', e => {
      e.preventDefault();
      const modalId = closeBtn.getAttribute('data-modal-close');
      const modal = document.getElementById(modalId);
      if (modal) {
        closeModal(modal);
      }
    });
  });

  // Close modal when clicking outside (on backdrop)
  document.addEventListener('click', e => {
    if (
      e.target.classList.contains('fixed') &&
      e.target.hasAttribute('role') &&
      e.target.getAttribute('role') === 'dialog'
    ) {
      closeModal(e.target);
    }
  });

  // Close modal on Escape key
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') {
      const openModal = document.querySelector('[role="dialog"]:not(.hidden)');
      if (openModal) {
        closeModal(openModal);
      }
    }
  });

  // Handle form submission
  downloadForms.forEach(form => {
    form.addEventListener('submit', async e => {
      e.preventDefault();
      await handleFormSubmission(form);
    });
  });

  /**
   * Close modal and reset state
   */
  function closeModal(modal) {
    modal.classList.add('hidden');
    modal.classList.remove('flex', 'items-center', 'justify-center');
    document.body.style.overflow = '';

    // Reset form state
    const form = modal.querySelector('.js-download-form');
    if (form) {
      form.reset();
      hideMessages(form);
    }
  }

  /**
   * Hide success and error messages
   */
  function hideMessages(form) {
    const successMsg = form.querySelector('.js-success-message');
    const errorMsg = form.querySelector('.js-error-message');
    const emailInput = form.querySelector('input[type="email"]');

    if (successMsg) successMsg.classList.add('hidden');
    if (errorMsg) errorMsg.classList.add('hidden');
    if (emailInput) {
      emailInput.setAttribute('aria-invalid', 'false');
      emailInput.classList.remove('text-erdbeere');
    }
  }

  /**
   * Show error message
   */
  function showError(form, message) {
    const errorMsg = form.querySelector('.js-error-message');
    const errorText = form.querySelector('.js-error-text');
    const successMsg = form.querySelector('.js-success-message');
    const emailInput = form.querySelector('input[type="email"]');

    if (successMsg) successMsg.classList.add('hidden');
    if (errorMsg) {
      if (errorText) errorText.textContent = message;
      errorMsg.classList.remove('hidden');
    }
    if (emailInput) {
      emailInput.setAttribute('aria-invalid', 'true');
      emailInput.classList.add('text-erdbeere');
    }
  }

  /**
   * Show success message
   */
  function showSuccess(form) {
    const successMsg = form.querySelector('.js-success-message');
    const errorMsg = form.querySelector('.js-error-message');
    const emailInput = form.querySelector('input[type="email"]');

    if (errorMsg) errorMsg.classList.add('hidden');
    if (successMsg) successMsg.classList.remove('hidden');
    if (emailInput) {
      emailInput.setAttribute('aria-invalid', 'false');
      emailInput.classList.remove('text-erdbeere');
    }
  }

  /**
   * Validate email format
   */
  function isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Subscribe email to Klaviyo list via the public Client Subscriptions API.
   * Uses only the public Company ID — safe to expose in the browser.
   * Docs: https://developers.klaviyo.com/en/reference/create_client_subscription
   */
  async function subscribeToKlaviyo(email, companyId, listId) {
    const response = await fetch(
      `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(companyId)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: '2024-10-15',
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Article download form',
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email },
                },
              },
            },
            relationships: {
              list: { data: { type: 'list', id: listId } },
            },
          },
        }),
      }
    );

    // Success: 202 Accepted with an empty body — do NOT try to parse JSON.
    if (response.status === 202) return;

    const errorData = await response.json().catch(() => ({}));
    const detail = errorData.errors && errorData.errors[0] && errorData.errors[0].detail;
    throw new Error(detail || `HTTP error! status: ${response.status}`);
  }

  /**
   * Trigger PDF download
   */
  function downloadPDF(url) {
    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = '';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Handle form submission
   */
  async function handleFormSubmission(form) {
    const emailInput = form.querySelector('input[type="email"]');
    const submitButton = form.querySelector('button[type="submit"]');
    const consentCheckbox = form.querySelector('.js-consent-checkbox');
    const downloadUrl = form.getAttribute('data-download-url');
    const companyId = form.getAttribute('data-klaviyo-company-id');
    const listId = form.getAttribute('data-klaviyo-list-id');
    const email = emailInput.value.trim();
    // If no checkbox is rendered, consent is implicit (legacy behavior).
    // If rendered, only subscribe to the Klaviyo list when the user ticked it.
    const consentGiven = consentCheckbox ? consentCheckbox.checked : true;

    // Hide previous messages
    hideMessages(form);

    // Validate email
    if (!email) {
      showError(form, 'Bitte gib deine E-Mail-Adresse ein.');
      emailInput.focus();
      return;
    }

    if (!isValidEmail(email)) {
      showError(form, 'Bitte gib eine gültige E-Mail-Adresse ein.');
      emailInput.focus();
      return;
    }

    // Only validate Klaviyo configuration if we actually plan to call it.
    if (consentGiven && (!companyId || !listId)) {
      showError(
        form,
        'Klaviyo-Konfiguration fehlt. Bitte kontaktiere den Administrator.'
      );
      return;
    }

    // Disable submit button during submission
    submitButton.disabled = true;
    submitButton.classList.add('opacity-50', 'cursor-not-allowed');

    try {
      // Only push to Klaviyo when the user actively consented (or no checkbox exists).
      if (consentGiven) {
        await subscribeToKlaviyo(email, companyId, listId);
      }

      // Show success message
      showSuccess(form);

      // Store email in localStorage to potentially skip modal on next visit
      try {
        localStorage.setItem('article-download-email', email);
      } catch (e) {
        // localStorage might not be available, ignore
      }

      // Wait a moment for user to see success message, then download
      setTimeout(() => {
        if (downloadUrl) {
          downloadPDF(downloadUrl);
        }

        // Close modal after a short delay
        const modal = form.closest('[role="dialog"]');
        if (modal) {
          setTimeout(() => {
            closeModal(modal);
          }, 1500);
        }
      }, 500);
    } catch (error) {
      console.error('[article-download] Klaviyo subscription failed:', error);
      // Show error message
      let errorMessage =
        'Es ist ein Fehler aufgetreten. Bitte versuche es erneut.';

      if (error.message.includes('HTTP error')) {
        if (error.message.includes('400')) {
          errorMessage =
            'Ungültige E-Mail-Adresse oder fehlende Klaviyo-Konfiguration.';
        } else if (
          error.message.includes('401') ||
          error.message.includes('403')
        ) {
          errorMessage =
            'Ungültige Klaviyo Company ID. Bitte kontaktiere den Administrator.';
        } else if (error.message.includes('404')) {
          errorMessage =
            'Klaviyo Liste nicht gefunden. Bitte kontaktiere den Administrator.';
        }
      }

      showError(form, errorMessage);
      emailInput.focus();
    } finally {
      // Re-enable submit button
      submitButton.disabled = false;
      submitButton.classList.remove('opacity-50', 'cursor-not-allowed');
    }
  }
});
